import { type LabelHTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/*
 * Label is a thin wrapper around <label> that injects the design-system
 * typography and optionally appends a required asterisk via `data-required`.
 * The asterisk is pure CSS so screen readers do not announce it; pair with
 * `aria-required` on the control instead.
 *
 * `data-required` is a plain HTML data attribute (string | undefined). React
 * renders a bare prop without a value as an empty string, which the CSS
 * [&[data-required]] selector still matches. No type override is needed;
 * LabelHTMLAttributes already accepts arbitrary data-* attributes.
 */
export type LabelProps = LabelHTMLAttributes<HTMLLabelElement>;

export function Label({ className, ...props }: LabelProps) {
  return (
    <label
      className={cn(
        'inline-block text-sm font-sans font-medium text-text',
        'leading-none',
        /* Asterisk appended via CSS pseudo-element when data-required is set */
        '[&[data-required]]:after:content-["*"] [&[data-required]]:after:ml-0.5 [&[data-required]]:after:text-danger',
        'data-[disabled=true]:opacity-50',
        className,
      )}
      {...props}
    />
  );
}
